import React from 'react';
import {
  BottomNavigation,
  BottomNavigationAction,
  Tooltip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Divider,
  ListItemButton,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Logout as LogoutIcon,
  MoreVert as MoreIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useThemeMode } from './theme';
import { useAuthStore } from './store/auth';
import { APP_MAIN_SX, APP_VIEWPORT_SX } from './layout/primitives';
import { appNavItems, getActiveNavItem } from './layout/navigation';

const RAIL_WIDTH = 76;
const RAIL_EXPANDED = 240;
const MOBILE_BAR_HORIZONTAL_PADDING = 'max(env(safe-area-inset-left, 0px), 16px)';

interface AppShellProps {
  children: React.ReactNode;
}

const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { mode, toggleTheme } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useAuthStore((s) => s.logout);
  const [pinned, setPinned] = React.useState(false);
  const [hovered, setHovered] = React.useState(false);
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);

  const expanded = pinned || hovered;
  const drawerWidth = expanded ? RAIL_EXPANDED : RAIL_WIDTH;
  const activeNavItem = getActiveNavItem(location.pathname);

  const handleNavigation = (path: string) => {
    navigate(path);
    if (!pinned) {
      setHovered(false);
    }
    setMenuAnchor(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const closeMenu = () => setMenuAnchor(null);

  const iconInset = `${(RAIL_WIDTH - 24) / 2}px`;
  const renderItem = (item: (typeof appNavItems)[number]) => {
    const Icon = item.icon;
    const selected = activeNavItem.path === item.path;

    return (
      <ListItem key={item.path} disablePadding sx={{ display: 'block' }}>
        <Tooltip title={expanded ? '' : item.label} placement="right" arrow>
          <ListItemButton
            selected={selected}
            onClick={() => handleNavigation(item.path)}
            sx={{
              minHeight: 48,
              px: 0,
              pl: iconInset,
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 24,
                mr: expanded ? 2 : 0,
                justifyContent: 'center',
              }}
            >
              <Icon />
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              sx={{ opacity: expanded ? 1 : 0, whiteSpace: 'nowrap' }}
            />
          </ListItemButton>
        </Tooltip>
      </ListItem>
    );
  };

  const accountMenu = (
    <Menu
      anchorEl={menuAnchor}
      open={Boolean(menuAnchor)}
      onClose={closeMenu}
      anchorOrigin={isDesktop
        ? { vertical: 'top', horizontal: 'right' }
        : { vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={isDesktop
        ? { vertical: 'bottom', horizontal: 'left' }
        : { vertical: 'top', horizontal: 'right' }}
    >
      <MenuItem
        onClick={() => {
          toggleTheme();
          closeMenu();
        }}
      >
        <ListItemIcon>
          {mode === 'dark' ? (
            <LightModeIcon fontSize="small" />
          ) : (
            <DarkModeIcon fontSize="small" />
          )}
        </ListItemIcon>
        <ListItemText>{mode === 'dark' ? 'Light mode' : 'Dark mode'}</ListItemText>
      </MenuItem>
      <Divider />
      <MenuItem
        onClick={() => {
          navigate('/login?reconnect=1');
          closeMenu();
        }}
      >
        <ListItemIcon>
          <SyncIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Reconnect Google</ListItemText>
      </MenuItem>
      <MenuItem
        onClick={() => {
          logout();
          closeMenu();
        }}
      >
        <ListItemIcon>
          <LogoutIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Sign out</ListItemText>
      </MenuItem>
    </Menu>
  );

  return (
    <Box
      sx={{
        ...APP_VIEWPORT_SX,
        flexDirection: { xs: 'column', md: 'row' },
      }}
    >
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: MOBILE_BAR_HORIZONTAL_PADDING,
          pr: 'max(env(safe-area-inset-right, 0px), 16px)',
          pt: 'max(env(safe-area-inset-top, 0px), 12px)',
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box
            component="img"
            src="/ah-logo.svg"
            alt="AH"
            sx={{ height: 32, width: 32, borderRadius: 1, flexShrink: 0 }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', lineHeight: 1.2 }}
            >
              AH Home
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
              {activeNavItem.label}
            </Typography>
          </Box>
        </Box>

        <IconButton
          aria-label="Account actions"
          onClick={handleMenuOpen}
          edge="end"
          sx={{ width: 44, height: 44, flexShrink: 0 }}
        >
          <MoreIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Navigation Rail */}
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          display: { xs: 'none', md: 'flex' },
          width: drawerWidth,
          flexShrink: 0,
          transition: 'width 200ms ease',
          overflow: 'hidden',
          flexDirection: 'column',
          minHeight: 0,
          bgcolor: 'background.paper',
          borderRight: 1,
          borderColor: 'divider',
          zIndex: theme.zIndex.drawer,
          position: 'relative',
        }}
      >
          {/* Logo / pin toggle */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: 48,
              pl: `${(RAIL_WIDTH - 32) / 2}px`,
              cursor: 'pointer',
              flexShrink: 0,
            }}
            onClick={() => setPinned((p) => !p)}
            title={pinned ? 'Collapse sidebar' : 'Pin sidebar open'}
          >
            <Box
              component="img"
              src="/ah-logo.svg"
              alt="AH"
              sx={{ height: 32, width: 32, borderRadius: 1, flexShrink: 0 }}
            />
          </Box>

        <Divider sx={{ mb: 0.5 }} />

        {/* Nav items */}
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          <List dense disablePadding>
            {appNavItems.map(renderItem)}
          </List>
        </Box>

        {/* Bottom actions */}
        <Divider />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            py: 1.5,
            px: 0.5,
          }}
        >
          <Tooltip title="More" placement="right">
            <IconButton
              size="small"
              aria-label="Account actions"
              onClick={handleMenuOpen}
            >
              <MoreIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          ...APP_MAIN_SX,
          width: '100%',
        }}
      >
        {children}
      </Box>

      <Box
        sx={{
          display: { xs: 'block', md: 'none' },
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          px: MOBILE_BAR_HORIZONTAL_PADDING,
          pr: 'max(env(safe-area-inset-right, 0px), 16px)',
          pb: 'max(env(safe-area-inset-bottom, 0px), 8px)',
          flexShrink: 0,
        }}
      >
        <BottomNavigation
          showLabels
          value={activeNavItem.path}
          onChange={(_event, value) => handleNavigation(value)}
          sx={{
            minHeight: 56,
            height: 'auto',
            bgcolor: 'transparent',
          }}
        >
          {appNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <BottomNavigationAction
                key={item.path}
                label={item.label}
                value={item.path}
                icon={<Icon />}
                sx={{
                  minWidth: 0,
                  pt: 1,
                }}
              />
            );
          })}
        </BottomNavigation>
      </Box>
      {accountMenu}
    </Box>
  );
};

export default AppShell;
